package com.milta.core.network

object Endpoints {
    private const val VERSION = "v1"
    private const val API = "api/$VERSION"

    /** GET, feature-flag style config shared by mobile and the web dashboard (RoutaR kill switch). */
    const val appConfig = "$API/app-config"

    const val profile = "$API/profile"
    const val profileLanguage = "$API/profile/language"
    const val profilePhoneRequest = "$API/profile/phone/request"
    const val profilePhoneConfirm = "$API/profile/phone/confirm"
    const val profileDeleteRequest = "$API/profile/delete/request"
    const val profileDeleteConfirm = "$API/profile/delete/confirm"
    /** MIL-49 — GET, Driver-only, returns the account's one-time linking code (idempotent). */
    const val profileLinkCode = "$API/profile/link-code"

    /** GET, `?query=` — address suggestions for the manual delivery entry screens. */
    const val addressAutocomplete = "$API/addresses/autocomplete"

    /**
     * GET (Manager team dashboard, MIL-38 — returns `TeamRoutesOverviewDto`, optional `?date=`) /
     * POST (create a route, DOM-02) on the same path `api/v1/routes`.
     */
    const val routes = "$API/routes"
    const val routeMe = "$API/routes/me"
    const val routeImport = "$API/routes/import"
    /** MIL-52 — GET, paginated route history for the web dashboard's "Historique des tournées"
     * (Screen A), optional `?from=&to=&driverId=&page=&pageSize=`. */
    const val routeHistory = "$API/routes/history"
    /** GET (route detail, Driver or Enterprise — MIL-38) / other verbs below on `api/v1/routes/{id}`. */
    fun route(id: String) = "$API/routes/$id"
    fun routeStart(id: String) = "$API/routes/$id/start"
    fun routeDeliveries(routeId: String) = "$API/routes/$routeId/deliveries"
    /** MIL-55 — DELETE a single stop from an in-flight route (écran 07b). */
    fun routeDelivery(routeId: String, deliveryId: String) = "$API/routes/$routeId/deliveries/$deliveryId"
    /** MIL-28 — manual entry, open to Driver + Manager, distinct from [routeDeliveries] (Enterprise-only bulk add). */
    fun routeManualDelivery(routeId: String) = "$API/routes/$routeId/deliveries/manual"
    fun routeOptimize(routeId: String) = "$API/routes/$routeId/optimize"
    /** MIL-45 — manually re-runs geocoding, e.g. after correcting an address. */
    fun routeRegeocode(routeId: String) = "$API/routes/$routeId/regeocode"
    /** POST /api/v1/routes/{routeId}/reoptimize — adds a manual stop AND re-orders the whole route in one call (MIL-35). */
    fun routeReoptimize(routeId: String) = "$API/routes/$routeId/reoptimize"
    /** POST /api/v1/routes/{routeId}/reoptimize-in-place — re-orders the existing stops, no new stop added (MIL-34, "Ré-optimiser"). */
    fun routeReoptimizeInPlace(routeId: String) = "$API/routes/$routeId/reoptimize-in-place"
    fun routeDispatch(routeId: String) = "$API/routes/$routeId/dispatch"
    /** MIL-36 — POST /routes/{id}/complete, driver-initiated end of route. */
    fun routeComplete(routeId: String) = "$API/routes/$routeId/complete"
    /** MIL-41 — POST proof of delivery (photo + status + comment) for a single stop. */
    fun routeDeliveryValidate(routeId: String, deliveryId: String) = "$API/routes/$routeId/deliveries/$deliveryId/validate"
    /** MIL-52 — GET, paginated proofs of one route for the web dashboard's "Preuves de la tournée"
     * (Screen B), optional `?page=&pageSize=`. */
    fun routeProofs(routeId: String) = "$API/routes/$routeId/proofs"

    /** POST (create a RouteBatch, MIL-39) on `api/v1/route-batches`. */
    const val routeBatches = "$API/route-batches"
    fun routeBatchReoptimize(batchId: String) = "$API/route-batches/$batchId/reoptimize"
    fun routeBatchDispatch(batchId: String) = "$API/route-batches/$batchId/dispatch"

    const val authRegisterOAuth = "$API/auth/register/oauth"
    const val authOAuthAuthenticate = "$API/auth/oauth/authenticate"
    const val authOAuthUpdatePhone = "$API/auth/oauth/update-phone"
    const val authOAuthAppleExchange = "$API/auth/oauth/apple/exchange"
    const val authOAuthGoogleExchange = "$API/auth/oauth/google/exchange"
    const val authRefresh = "$API/auth/refresh"
    const val authLogout = "$API/auth/logout"
    const val authLogoutAll = "$API/auth/logout-all"
    const val authOtpRequest = "$API/auth/otp/send"
    const val authOtpVerify = "$API/auth/otp/verify"
    const val authCheck = "$API/auth/check"

    // Backward-compat alias
    const val authOtpSend = authOtpRequest

    const val myTeam = "$API/enterprises/my-team"
    /** MIL-53 — GET (profil en lecture seule) / DELETE (suppression définitive) sur le même chemin. */
    const val myEnterprise = "$API/enterprises/me"
    fun enterpriseDrivers(enterpriseId: String) = "$API/enterprises/$enterpriseId/drivers"
    /** MIL-49 — POST body `{code}`, links a driver to the enterprise via their one-time code. */
    fun teamLink(enterpriseId: String) = "$API/enterprises/$enterpriseId/team/link"
    /** MIL-49 — DELETE, breaks the Membership only (the driver's Milta account stays untouched). */
    fun teamMember(enterpriseId: String, driverId: String) = "$API/enterprises/$enterpriseId/team/$driverId"

    const val subscriptionMe      = "$API/subscriptions/me"
    const val subscriptionReceipt = "$API/subscriptions/receipt"
    const val subscriptionPlans   = "$API/subscriptions/plans"

    const val imports = "$API/imports"
    fun importRetry(importSessionId: String) = "$API/imports/$importSessionId/retry"

    /** POST /api/v1/imports/pdf — single synchronous Claude Haiku extraction of a PDF delivery note, no session (MIL-26). */
    const val importsPdf = "$API/imports/pdf"

    /** POST /api/v1/ocr-scans — Claude Vision scan of a photographed paper delivery list (DOM-02, no session). */
    const val ocrScans = "$API/ocr-scans"

    /** POST /api/v1/package-label-scans — Claude Vision scan of a photographed package shipping label (MIL-25, no session). */
    const val packageLabelScans = "$API/package-label-scans"

    /** POST /api/v1/spreadsheet-imports — server-side AI column mapping of an uploaded Excel/CSV file (MIL-27, no session). */
    const val spreadsheetImports = "$API/spreadsheet-imports"
}
